// ---------------------------------------------------------------------------
// Shared site content and per-route SEO metadata.
//
// Deliberately CommonJS and JSX-free so the exact same copy can be consumed by
// two very different callers:
//   - the React app (webpack interop handles the CJS import), and
//   - scripts/prerender.mjs, which runs in plain Node at build time.
//
// That shared-source rule is the whole point of this file: the static HTML we
// serve to a crawler is generated from the same strings the browser renders,
// so the two can't drift apart. Serving a crawler something the user doesn't
// see is cloaking, and it's a far worse policy problem than the thin-content
// one this prerendering exists to solve.
// ---------------------------------------------------------------------------

const { DISCOUNTS_ENABLED, VIDEOS_ENABLED } = require('./features');

const ORIGIN = 'https://volcyy.com';
const SITE_NAME = 'Volcyy';
const DEFAULT_OG_IMAGE = `${ORIGIN}/assets/social-preview.png`;
const CONTACT_EMAIL = 'contact@volcyy.com';
const TAGLINE = DISCOUNTS_ENABLED
  ? 'Exclusive restaurant discounts worldwide.'
  : 'Diabolical Reviews for Bars and Nightclubs';

// Legal copy lives here rather than inline in the page components so the
// prerendered HTML carries the real, full policy text — these are the pages a
// policy reviewer actually reads, and an empty shell is worse than useless.
const DISCOUNT_PRIVACY_SECTIONS = [
  {
    heading: 'Information We Collect',
    body: 'We collect information you provide directly, including your name, email address, and payment information when you create an account or subscribe. We also collect usage data such as pages visited and features used.',
  },
  {
    heading: 'How We Use Your Information',
    body: 'We use your information to provide and improve the Volcyy service, process your subscription, communicate with you about your account, and send you relevant updates. We do not sell your personal information to third parties.',
  },
  {
    heading: 'Membership Data',
    body: 'Your membership ID and status are stored securely and used solely to verify your eligibility for restaurant discounts. QR code verification shows only your name and membership status to restaurant staff.',
  },
  {
    heading: 'Cookies and Analytics',
    body: 'Volcyy uses cookies and similar technologies to keep you signed in, remember your saved location, and measure how the site is used. Volcyy does not display advertising and does not use advertising cookies. We use your browser timezone as a conservative regional hint: visitors in Europe, Brazil and unknown zones are asked before analytics storage is enabled. Elsewhere analytics is enabled by default. You can opt out or change your choice at any time through Cookie Settings in the footer.',
  },
  {
    heading: 'Data Security',
    body: 'We use industry-standard security measures, including Firebase Authentication and Firestore, to protect your data. Your password is encrypted and never stored in plain text.',
  },
  {
    heading: 'Third-Party Services',
    body: 'We use Google Firebase for authentication and data storage, Stripe for payment processing, Google Maps and Google Places for address lookup and listing information, Brevo for account emails, and Google Analytics to measure visits. Google Firebase and Google Cloud host our services. Providers process information under their own privacy policies.',
  },
  {
    heading: 'Your Rights',
    body: `You may request deletion of your account and associated data at any time by contacting us at ${CONTACT_EMAIL}.`,
  },
  {
    heading: 'Contact',
    body: `For privacy-related inquiries, contact us at ${CONTACT_EMAIL}.`,
  },
];

const DISCOUNT_TERMS_SECTIONS = [
  {
    heading: 'Acceptance of Terms',
    body: 'By using Volcyy, you agree to these Terms of Service. If you do not agree, please do not use the service.',
  },
  {
    heading: 'Membership',
    body: 'Volcyy is free to join and gives every registered member access to discounts at participating partner restaurants. Volcyy Black is an optional paid upgrade billed at $1.99 per month that unlocks bigger discounts at the same restaurants. Membership is personal and non-transferable. Sharing your membership card or redemption QR code constitutes a violation of these terms.',
  },
  {
    heading: 'Cancellation & Refunds',
    body: 'You may cancel your Volcyy Black subscription at any time. Upon cancellation, Black benefits remain active until the end of the current billing period, after which your account reverts to the free tier. Refunds are not provided for partial periods.',
  },
  {
    heading: 'Discount Usage',
    body: 'Discounts are available only at participating Volcyy partner restaurants during their listed hours. Each discount can be redeemed once per day per restaurant. Volcyy is not responsible if a restaurant changes its participation status. Discounts cannot be combined with other offers unless explicitly stated.',
  },
  {
    heading: 'Restaurant Partner Changes',
    body: 'The list of partner restaurants may change at any time. Volcyy does not guarantee the availability of any specific restaurant as a partner.',
  },
  {
    heading: 'Redemption Codes',
    body: 'Each redemption generates a single-use QR code that is valid only at the selected restaurant and expires ten minutes after it is issued. Codes are verified in real time and reflect your current membership status. Fraudulent use of a Volcyy code may result in account termination.',
  },
  {
    heading: 'Limitation of Liability',
    body: 'Volcyy is not liable for any indirect, incidental, or consequential damages arising from use of the service. Our total liability shall not exceed the amount paid by you in the past 12 months.',
  },
  {
    heading: 'Contact',
    body: `Questions about these terms? Contact us at ${CONTACT_EMAIL}.`,
  },
];

// Review-era legal copy (September 2026 pivot). The discount-era versions above
// are kept verbatim for when DISCOUNTS_ENABLED comes back on.
const REVIEW_PRIVACY_SECTIONS = [
  {
    heading: 'Information We Collect',
    body: `If you create an account we collect your name, email address and the handle you choose. If you post without an account we create a guest account for you, identified only by a random ID and an auto-generated handle. We also collect the reviews, ratings, comments, replies, likes${VIDEOS_ENABLED ? ' and video links' : ''} you post, and usage data such as pages visited.`,
  },
  {
    heading: 'What Is Public',
    body: `Reviews, ratings, comments${VIDEOS_ENABLED ? ', replies and video links' : ' and replies'} you post are public, along with your handle. Anyone can read them, share them and take screenshots of them. Do not post anything you would not want seen publicly.`,
  },
  {
    heading: 'How We Use Your Information',
    body: 'We use your information to run Volcyy, display what you post, keep your guest activity attached to your account when you claim it, prevent spam and abuse, and communicate with you about your account. We do not sell your personal information to third parties.',
  },
  {
    heading: 'Spam and Abuse Prevention',
    body: 'To stop spam and fake activity we record a one-way hash of the IP address and a device check with each post, rating and like. This information is used only for abuse prevention and is never shown publicly.',
  },
  VIDEOS_ENABLED && {
    heading: 'Embedded Videos',
    body: 'New video submissions on Volcyy use TikTok embeds. Older videos may still use other platforms. When you play one, that platform may set cookies and collect data under its own privacy policy.',
  },
  {
    heading: 'Cookies and Analytics',
    body: 'Volcyy uses cookies and similar technologies to keep you signed in, remember your saved location, and measure how the site is used. We ask for your consent before analytics cookies are set, and you can change or withdraw that consent at any time from the cookie settings link in the site footer.',
  },
  {
    heading: 'Third-Party Services',
    body: 'We use Google Firebase for authentication, data storage and spam protection (including Google reCAPTCHA), Google Maps for address lookup, and Google Analytics to measure how the site is used. These services have their own privacy policies governing their use of your data.',
  },
  {
    heading: 'Business Claims and Retention',
    body: 'Claims may include private images or PDF documents and business contact details used to verify authority to manage a listing. Only the claimant and authorized administrators can access uploads. We retain proof while needed to verify and administer the claim or resolve a dispute, then delete it when no longer needed. Contact us to request deletion. Legal obligations or active disputes may require limited continued retention.',
  },
  {
    heading: 'Listing Sources',
    body: 'Listing details may come from business owners, administrators and Google Places. Imported listings may include public contact information, opening hours, editorial summaries and attributed photos. Contact us to correct a listing. Google services are subject to Google Terms of Service and Privacy Policy at https://policies.google.com/terms and https://policies.google.com/privacy.',
  },
  {
    heading: 'Your Rights',
    body: `Depending on your location, you may request access, correction, export, restriction, objection or deletion of personal information, withdraw consent, or complain to your local privacy authority. Contact ${CONTACT_EMAIL}.`,
  },
  {
    heading: 'Contact',
    body: `For privacy-related inquiries, contact us at ${CONTACT_EMAIL}.`,
  },
];

const REVIEW_TERMS_SECTIONS = [
  {
    heading: 'Acceptance of Terms',
    body: 'By using Volcyy, you agree to these Terms of Service. If you do not agree, please do not use the service.',
  },
  {
    heading: 'Age Requirement',
    body: 'Volcyy contains unfiltered, explicit language posted by users. You must be at least 18 years old to use Volcyy or to post on it.',
  },
  {
    heading: 'Your Content',
    body: 'You keep ownership of what you post. By posting, you give Volcyy a worldwide, royalty-free license to host, display, reproduce and share it, including in Volcyy marketing and on social media. You are responsible for what you post.',
  },
  {
    heading: 'What You Can Post',
    body: 'Volcyy is for real, unfiltered reactions to real venue visits. Strong language is allowed. Reviews and ratings must reflect your own genuine experience.',
  },
  {
    heading: 'What You Cannot Post',
    body: "You may not post: sexual comments about, or harassment of, venue staff or any other identifiable person; threats; hate speech; anyone's personal information; spam or advertising; reviews or ratings for a venue you have not been to, or that you were paid or rewarded to write; reviews of your own venue or a competitor's; or anything illegal. We may hide or remove content and suspend accounts that break these rules.",
  },
  {
    heading: 'Guest Accounts',
    body: 'You can post without signing up. Your posts are kept under a guest account on this device, and you can claim that account later with an email address or Google sign-in to keep everything you posted. If you clear your browser data before claiming, your guest account and the ability to manage its posts may be lost.',
  },
  {
    heading: 'Ratings',
    body: "Each person can give one star rating per venue. A venue's rating is the average of those ratings. We may exclude ratings we reasonably believe are fake, duplicated or automated.",
  },
  {
    heading: 'Business Accounts',
    body: 'Business owners can claim their listing after verification. A claimed venue can update its information and reply to reviews as the owner. Venues cannot edit or remove user reviews, and may not offer anything in exchange for reviews or ratings.',
  },
  VIDEOS_ENABLED && {
    heading: 'Videos',
    body: 'Only share links to public videos on supported platforms. Videos are embedded from, and remain subject to the terms of, the platform they are hosted on. If you are the creator of a video and want it removed from Volcyy, contact us.',
  },
  {
    heading: 'Reporting and Takedowns',
    body: `Use the report button on any post, or contact ${CONTACT_EMAIL} to report content or send a copyright or legal takedown request.`,
  },
  {
    heading: 'Limitation of Liability',
    body: 'Reviews and comments are the opinions of the users who post them, not of Volcyy. Volcyy is not liable for any indirect, incidental, or consequential damages arising from use of the service.',
  },
  {
    heading: 'Contact',
    body: `Questions about these terms? Contact us at ${CONTACT_EMAIL}.`,
  },
];

// The video sections above are `false` while VIDEOS_ENABLED is off.
const PRIVACY_SECTIONS = DISCOUNTS_ENABLED ? DISCOUNT_PRIVACY_SECTIONS : REVIEW_PRIVACY_SECTIONS.filter(Boolean);
const TERMS_SECTIONS = DISCOUNTS_ENABLED ? DISCOUNT_TERMS_SECTIONS : REVIEW_TERMS_SECTIONS.filter(Boolean);

const LEGAL_LAST_UPDATED = DISCOUNTS_ENABLED ? 'August 2026' : 'October 2026';

// Per-route SEO metadata.
//
// `index: false` marks the purely functional screens — checkout, admin, the
// scanner, the redemption screen. They have no publisher content by design, so
// they are kept out of the sitemap and served with a noindex robots tag.
const ROUTE_META = {
  '/': {
    title: 'Volcyy | Free Restaurant Discounts',
    description:
      'Save money at restaurants worldwide with Volcyy. Join for free to unlock exclusive restaurant discounts and enjoy great deals.',
    h1: 'Exclusive restaurant discounts await you',
    intro: 'Join Volcyy for free and unlock exclusive discounts at restaurants worldwide.',
    index: true,
  },
  '/benefits': {
    title: 'Membership Benefits & Pricing | Volcyy',
    description:
      'Compare the free Volcyy membership with Volcyy Black at $1.99/month. See what each tier includes and how much you can save at partner restaurants.',
    h1: 'Free to join. Endless savings.',
    intro: 'Join thousands of members worldwide who save money every time they eat out.',
    index: true,
  },
  '/contact': {
    title: 'Contact Volcyy | Support & Feedback',
    description:
      'Questions about your membership, a partner restaurant, or a discount? Get in touch with the Volcyy team.',
    h1: 'Get in Touch',
    intro: "Have a question or feedback? We'd love to hear from you.",
    index: true,
  },
  '/business': {
    title: 'List Your Business on Volcyy | For Business Owners',
    description:
      'Reach new diners by offering an exclusive discount to Volcyy members. Learn how restaurant partnerships work and list your restaurant for free.',
    h1: 'List your restaurant on Volcyy',
    intro:
      'Reach more customers by offering exclusive discounts to Volcyy members and turn first-time visitors into loyal customers.',
    index: true,
  },
  '/privacy': {
    title: 'Privacy Policy | Volcyy',
    description:
      'How Volcyy collects, uses, and protects your personal data, including our use of cookies and analytics.',
    h1: 'Privacy Policy',
    intro: `Last updated: ${LEGAL_LAST_UPDATED}`,
    sections: PRIVACY_SECTIONS,
    index: true,
  },
  '/terms': {
    title: 'Terms of Service | Volcyy',
    description:
      'The terms governing Volcyy memberships, discount redemption, Volcyy Black subscriptions, cancellations, and refunds.',
    h1: 'Terms of Service',
    intro: `Last updated: ${LEGAL_LAST_UPDATED}`,
    sections: TERMS_SECTIONS,
    index: true,
  },

  // Functional / account screens — no publisher content, never indexed.
  '/profile': {
    title: 'Your Profile | Volcyy',
    description: 'View your Volcyy membership and redemption history.',
    h1: 'Your Profile',
    index: false,
  },
  '/checkout': {
    title: 'Checkout | Volcyy',
    description: 'Complete your Volcyy Black subscription.',
    h1: 'Checkout',
    index: false,
  },
  '/reset-password': {
    title: 'Reset Your Password | Volcyy',
    description: 'Reset the password for your Volcyy account.',
    h1: 'Reset Your Password',
    index: false,
  },
  '/admin': {
    title: 'Admin | Volcyy',
    description: 'Volcyy administration.',
    h1: 'Admin',
    index: false,
  },
  '/business/onboarding': {
    title: 'List Your Business | Volcyy',
    description: 'Submit your business listing to Volcyy.',
    h1: 'List Your Business',
    index: false,
  },
  '/business/dashboard': {
    title: 'Business Dashboard | Volcyy',
    description: 'Manage your Volcyy business listing.',
    h1: 'Business Dashboard',
    index: false,
  },
  '/business/scan': {
    title: 'Scan a Redemption Code | Volcyy',
    description: 'Scan a member redemption code.',
    h1: 'Scan a Redemption Code',
    index: false,
  },
};

// The pages whose copy is about discounts. Merged over ROUTE_META while
// DISCOUNTS_ENABLED is off; the discount-era wording above stays untouched.
const REVIEW_ROUTE_META = {
  '/': {
    title: 'Diabolical Reviews for Bars and Nightclubs',
    description: 'No Cap. Just Chaos. All the Tea. Discover the Best Bars and Nightclubs with Volcyy.',
    h1: 'Diabolical Reviews for Bars and Nightclubs',
    intro: 'No Cap. Just Chaos. All the Tea. Discover the Best Bars and Nightclubs with Volcyy.',
    index: true,
  },
  '/contact': {
    title: 'Contact Volcyy | Support & Feedback',
    description:
      'Questions about a review, a venue listing, or your account? Get in touch with the Volcyy team.',
    h1: 'Get in Touch',
    intro: "Have a question or feedback? We'd love to hear from you.",
    index: true,
  },
  '/business': {
    title: 'List Your Business on Volcyy | For Business Owners',
    description:
      'People check Volcyy to see what people are really saying. List your venue for free, add your social media, and reply to reviews as the owner.',
    h1: 'Your venue is already being talked about',
    intro:
      'List your venue on Volcyy for free, add your social media, and reply to reviews as the owner.',
    index: true,
  },
  '/terms': {
    title: 'Terms of Service | Volcyy',
    description:
      `The rules for posting reviews, ratings${VIDEOS_ENABLED ? ', comments and videos' : ' and comments'} on Volcyy, guest accounts, and venue accounts.`,
    h1: 'Terms of Service',
    intro: `Last updated: ${LEGAL_LAST_UPDATED}`,
    sections: TERMS_SECTIONS,
    index: true,
  },
  '/profile': {
    title: 'Your Profile | Volcyy',
    description: 'Your Volcyy account.',
    h1: 'Your Profile',
    index: false,
  },
};

if (!DISCOUNTS_ENABLED) {
  Object.assign(ROUTE_META, REVIEW_ROUTE_META);
  // Redirected to / by the router while discounts are off, so they must not
  // be in the sitemap or get a prerendered page.
  delete ROUTE_META['/benefits'];
  delete ROUTE_META['/checkout'];
  delete ROUTE_META['/business/scan'];
}

// Restaurant detail pages are generated per listing but share this shape.
function restaurantMeta(r) {
  const place = [r.city, r.state].filter(Boolean).join(', ');
  const cuisine = r.cuisine || 'Venue';
  const best = Math.max(Number(r.freeDiscount) || 0, Number(r.blackDiscount) || 0);

  const titleParts = [r.name];
  if (place) titleParts.push(`${cuisine} in ${place}`);
  else titleParts.push(cuisine);
  titleParts.push(SITE_NAME);

  const descBits = [];
  if (!DISCOUNTS_ENABLED) {
    const count = Number(r.ratingCount) || 0;
    const avg = Number(r.ratingAvg) || 0;
    if (count > 0) {
      descBits.push(`Rated ${avg.toFixed(1)} out of 5 from ${count} ${count === 1 ? 'review' : 'reviews'}.`);
    }
    descBits.push(
      place
        ? `Read wild reviews${VIDEOS_ENABLED ? ' and watch videos' : ''} of ${r.name} in ${place} on Volcyy.`
        : `Read wild reviews${VIDEOS_ENABLED ? ' and watch videos' : ''} of ${r.name} on Volcyy.`
    );
    if (r.description) descBits.push(r.description.trim().replace(/\s+/g, ' '));
  } else if (r.description) {
    descBits.push(r.description.trim().replace(/\s+/g, ' '));
  }
  if (DISCOUNTS_ENABLED && best > 0) {
    descBits.push(
      place
        ? `Save up to ${best}% at ${r.name} in ${place} with a free Volcyy membership.`
        : `Save up to ${best}% at ${r.name} with a free Volcyy membership.`
    );
  }

  return {
    title: titleParts.join(' | '),
    description: descBits.join(' ').slice(0, 300),
    canonicalPath: `/business/${r.id}`,
    index: true,
  };
}

module.exports = {
  ORIGIN,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  CONTACT_EMAIL,
  TAGLINE,
  PRIVACY_SECTIONS,
  TERMS_SECTIONS,
  LEGAL_LAST_UPDATED,
  ROUTE_META,
  restaurantMeta,
};
